import { Injectable } from '@angular/core';
import { LocalStorageService } from '../localStorage/local-storage.service';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly THEME_KEY = 'app_theme';
  private readonly DARK_CLASS = 'dark-theme';
  isDarkMode = false;
  private styleElement: HTMLStyleElement | null = null;

  constructor(private localStorageService: LocalStorageService) {}

  init(): void {
    const savedTheme = this.localStorageService.getKey(this.THEME_KEY);
    // Force default to light mode instead of checking OS prefers-color-scheme
    this.isDarkMode = savedTheme ? savedTheme === 'dark' : false;
    this.applyTheme(this.isDarkMode);
  }

  toggle(): void {
    this.isDarkMode = !this.isDarkMode;
    this.applyTheme(this.isDarkMode);
    this.localStorageService.setKey(this.THEME_KEY, this.isDarkMode ? 'dark' : 'light');
  }

  private applyTheme(dark: boolean): void {
    document.body.classList.toggle(this.DARK_CLASS, dark);
    if(dark){
      this.injectStyles();
    }else{
      this.removeStyles();
    }
  }

  private injectStyles(): void {
    if (this.styleElement) return;
    this.styleElement = document.createElement('style');
    this.styleElement.id = 'dark-mode-styles';
    this.styleElement.textContent = this.getDarkStyles();
    document.head.appendChild(this.styleElement);
  }

  private removeStyles(): void {
    if (this.styleElement) {
      this.styleElement.remove();
      this.styleElement = null;
    }
  }

  private getDarkStyles(): string {
    return `
      body.dark-theme {
        background-color: #0a0a0a !important;
        color: #ece7e7 !important;
      }

      /* Force transparent background on everything by default to override hardcoded white backgrounds */
      body.dark-theme *:where(:not(.note-editable):not(.note-editable *):not(.NgxEditor__Content):not(.NgxEditor__Content *):not(.NgxEditor__Popup):not(.NgxEditor__Popup *):not(.NgxEditor__Dropdown):not(.NgxEditor__Dropdown *):not(.note-dropdown-menu):not(.note-dropdown-menu *):not(.notification-banner):not(.notification-banner *):not(.email-content-scoped):not(.email-content-scoped *):not(.styled-comment):not(.styled-comment *):not(.away-card-icon):not(.badge):not(.item-badge):not([class*="text-bg-"]):not([class*="status-badge"]):not([class*="status-success"]):not([class*="status-pending"]):not([class*="status-not-approved"]):not([class*="status-process"]):not(.chip):not(.spinner):not(th):not([class*="mat-mdc-header"]):not([class*="mat-header"]):not(.away-lock-screen):not(.away-lock-screen *)) {
        background-color: transparent !important;
        border-color: #3a3a3a !important;
      }

      /* Global resets for text colors with !important to override inline email styles */
      body.dark-theme div:where(:not(.note-editable):not(.note-editable *):not(.NgxEditor__Content):not(.NgxEditor__Content *):not(.NgxEditor__Popup):not(.NgxEditor__Popup *):not(.NgxEditor__Dropdown):not(.NgxEditor__Dropdown *):not(.note-dropdown-menu):not(.note-dropdown-menu *):not(.notification-banner):not(.notification-banner *):not(.email-content-scoped):not(.email-content-scoped *):not(.styled-comment):not(.styled-comment *):not(.badge):not(.badge *):not(button *):not(.mat-mdc-button *):not([class*="status-process"]):not([class*="status-success"]):not([class*="status-pending"]):not([class*="status-not-approved"])), 
      body.dark-theme span:where(:not(.note-editable):not(.note-editable *):not(.NgxEditor__Content):not(.NgxEditor__Content *):not(.NgxEditor__Popup):not(.NgxEditor__Popup *):not(.NgxEditor__Dropdown):not(.NgxEditor__Dropdown *):not(.note-dropdown-menu):not(.note-dropdown-menu *):not(.notification-banner):not(.notification-banner *):not(.email-content-scoped):not(.email-content-scoped *):not(.styled-comment):not(.styled-comment *):not(.badge):not(.badge *):not(button *):not(.mat-mdc-button *):not([class*="status-process"]):not([class*="status-success"]):not([class*="status-pending"]):not([class*="status-not-approved"])), 
      body.dark-theme p:where(:not(.note-editable):not(.note-editable *):not(.NgxEditor__Content):not(.NgxEditor__Content *):not(.NgxEditor__Popup):not(.NgxEditor__Popup *):not(.NgxEditor__Dropdown):not(.NgxEditor__Dropdown *):not(.note-dropdown-menu):not(.note-dropdown-menu *):not(.notification-banner):not(.notification-banner *):not(.email-content-scoped):not(.email-content-scoped *):not(.styled-comment):not(.styled-comment *):not(.badge):not(.badge *):not(button *):not(.mat-mdc-button *):not([class*="status-process"]):not([class*="status-success"]):not([class*="status-pending"]):not([class*="status-not-approved"])), 
      body.dark-theme h1:where(:not(.note-editable):not(.note-editable *):not(.NgxEditor__Content):not(.NgxEditor__Content *):not(.NgxEditor__Popup):not(.NgxEditor__Popup *):not(.NgxEditor__Dropdown):not(.NgxEditor__Dropdown *):not(.note-dropdown-menu):not(.note-dropdown-menu *):not(.notification-banner):not(.notification-banner *):not(.email-content-scoped):not(.email-content-scoped *):not(.styled-comment):not(.styled-comment *)), 
      body.dark-theme h2:where(:not(.note-editable):not(.note-editable *):not(.NgxEditor__Content):not(.NgxEditor__Content *):not(.NgxEditor__Popup):not(.NgxEditor__Popup *):not(.NgxEditor__Dropdown):not(.NgxEditor__Dropdown *):not(.note-dropdown-menu):not(.note-dropdown-menu *):not(.notification-banner):not(.notification-banner *):not(.email-content-scoped):not(.email-content-scoped *):not(.styled-comment):not(.styled-comment *)), 
      body.dark-theme h3:where(:not(.note-editable):not(.note-editable *):not(.NgxEditor__Content):not(.NgxEditor__Content *):not(.NgxEditor__Popup):not(.NgxEditor__Popup *):not(.NgxEditor__Dropdown):not(.NgxEditor__Dropdown *):not(.note-dropdown-menu):not(.note-dropdown-menu *):not(.notification-banner):not(.notification-banner *):not(.email-content-scoped):not(.email-content-scoped *):not(.styled-comment):not(.styled-comment *)), 
      body.dark-theme h4:where(:not(.note-editable):not(.note-editable *):not(.NgxEditor__Content):not(.NgxEditor__Content *):not(.NgxEditor__Popup):not(.NgxEditor__Popup *):not(.NgxEditor__Dropdown):not(.NgxEditor__Dropdown *):not(.note-dropdown-menu):not(.note-dropdown-menu *):not(.notification-banner):not(.notification-banner *):not(.email-content-scoped):not(.email-content-scoped *):not(.styled-comment):not(.styled-comment *)), 
      body.dark-theme h5:where(:not(.note-editable):not(.note-editable *):not(.NgxEditor__Content):not(.NgxEditor__Content *):not(.NgxEditor__Popup):not(.NgxEditor__Popup *):not(.NgxEditor__Dropdown):not(.NgxEditor__Dropdown *):not(.note-dropdown-menu):not(.note-dropdown-menu *):not(.notification-banner):not(.notification-banner *):not(.email-content-scoped):not(.email-content-scoped *):not(.styled-comment):not(.styled-comment *)), 
      body.dark-theme h6:where(:not(.note-editable):not(.note-editable *):not(.NgxEditor__Content):not(.NgxEditor__Content *):not(.NgxEditor__Popup):not(.NgxEditor__Popup *):not(.NgxEditor__Dropdown):not(.NgxEditor__Dropdown *):not(.note-dropdown-menu):not(.note-dropdown-menu *):not(.notification-banner):not(.notification-banner *):not(.email-content-scoped):not(.email-content-scoped *):not(.styled-comment):not(.styled-comment *)), 
      body.dark-theme li:where(:not(.note-editable):not(.note-editable *):not(.NgxEditor__Content):not(.NgxEditor__Content *):not(.NgxEditor__Popup):not(.NgxEditor__Popup *):not(.NgxEditor__Dropdown):not(.NgxEditor__Dropdown *):not(.note-dropdown-menu):not(.note-dropdown-menu *):not(.notification-banner):not(.notification-banner *):not(.email-content-scoped):not(.email-content-scoped *):not(.styled-comment):not(.styled-comment *)), 
      body.dark-theme td:where(:not(.note-editable):not(.note-editable *):not(.NgxEditor__Content):not(.NgxEditor__Content *):not(.NgxEditor__Popup):not(.NgxEditor__Popup *):not(.NgxEditor__Dropdown):not(.NgxEditor__Dropdown *):not(.note-dropdown-menu):not(.note-dropdown-menu *):not(.notification-banner):not(.notification-banner *):not(.email-content-scoped):not(.email-content-scoped *):not(.styled-comment):not(.styled-comment *)), 
      body.dark-theme th:where(:not(.note-editable):not(.note-editable *):not(.NgxEditor__Content):not(.NgxEditor__Content *):not(.NgxEditor__Popup):not(.NgxEditor__Popup *):not(.NgxEditor__Dropdown):not(.NgxEditor__Dropdown *):not(.note-dropdown-menu):not(.note-dropdown-menu *):not(.notification-banner):not(.notification-banner *):not(.email-content-scoped):not(.email-content-scoped *):not(.styled-comment):not(.styled-comment *)), 
      body.dark-theme strong:where(:not(.note-editable):not(.note-editable *):not(.NgxEditor__Content):not(.NgxEditor__Content *):not(.NgxEditor__Popup):not(.NgxEditor__Popup *):not(.NgxEditor__Dropdown):not(.NgxEditor__Dropdown *):not(.note-dropdown-menu):not(.note-dropdown-menu *):not(.notification-banner):not(.notification-banner *):not(.email-content-scoped):not(.email-content-scoped *):not(.styled-comment):not(.styled-comment *)), 
      body.dark-theme b:where(:not(.note-editable):not(.note-editable *):not(.NgxEditor__Content):not(.NgxEditor__Content *):not(.NgxEditor__Popup):not(.NgxEditor__Popup *):not(.NgxEditor__Dropdown):not(.NgxEditor__Dropdown *):not(.note-dropdown-menu):not(.note-dropdown-menu *):not(.notification-banner):not(.notification-banner *):not(.email-content-scoped):not(.email-content-scoped *):not(.styled-comment):not(.styled-comment *)), 
      body.dark-theme i:where(:not(.note-editable):not(.note-editable *):not(.NgxEditor__Content):not(.NgxEditor__Content *):not(.NgxEditor__Popup):not(.NgxEditor__Popup *):not(.NgxEditor__Dropdown):not(.NgxEditor__Dropdown *):not(.note-dropdown-menu):not(.note-dropdown-menu *):not(.notification-banner):not(.notification-banner *):not(.email-content-scoped):not(.email-content-scoped *):not(.styled-comment):not(.styled-comment *)),
      body.dark-theme label:where(:not(.note-editable):not(.note-editable *):not(.NgxEditor__Content):not(.NgxEditor__Content *):not(.NgxEditor__Popup):not(.NgxEditor__Popup *):not(.NgxEditor__Dropdown):not(.NgxEditor__Dropdown *):not(.note-dropdown-menu):not(.note-dropdown-menu *):not(.notification-banner):not(.notification-banner *):not(.email-content-scoped):not(.email-content-scoped *):not(.styled-comment):not(.styled-comment *)),
      body.dark-theme a:where(:not(.note-editable):not(.note-editable *):not(.NgxEditor__Content):not(.NgxEditor__Content *):not(.NgxEditor__Popup):not(.NgxEditor__Popup *):not(.NgxEditor__Dropdown):not(.NgxEditor__Dropdown *):not(.note-dropdown-menu):not(.note-dropdown-menu *):not(.notification-banner):not(.notification-banner *):not(.email-content-scoped):not(.email-content-scoped *):not(.styled-comment):not(.styled-comment *)) {
        color: #ece7e7 !important;
      }
      body.dark-theme input, body.dark-theme textarea, body.dark-theme .mdc-text-field,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-chip-input,
      body.dark-theme.dark-theme.dark-theme .note-toolbar * {
        color: #ece7e7 !important;
      }
      body.dark-theme.dark-theme.dark-theme .mat-mdc-dialog-title,
      body.dark-theme.dark-theme.dark-theme .mat-dialog-title {
        color: #ece7e7 !important;
      }

      body.dark-theme .mat-mdc-icon-button mat-icon, 
      body.dark-theme button mat-icon {
        color: #ece7e7 !important;
      }

      body.dark-theme.dark-theme.dark-theme img[src*="-blue.svg"], 
      body.dark-theme.dark-theme.dark-theme img[src*="-dark.svg"],
      body.dark-theme.dark-theme.dark-theme img[src*="-gray.svg"],
      body.dark-theme.dark-theme.dark-theme img[src*="-line.svg"]:not([src*="phone-line.svg"]),
      body.dark-theme.dark-theme.dark-theme img[src*="circle-dots-vertical.svg"],
      body.dark-theme.dark-theme.dark-theme img[src*="add.svg"] {
        filter: brightness(0) invert(1) !important;
      }

      /* Bright Colors - Bumping specificity to override global text colors */
      body.dark-theme.dark-theme.dark-theme .mat-mdc-button.active,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-button.active * {
        color: #FF6525 !important;
      }

      body.dark-theme.dark-theme.dark-theme .text-bg-secondary {
        color: #ece7e7 !important;
      }
      
      body.dark-theme.dark-theme.dark-theme .text-style {
        color: #12C5AD !important;
      }

      /* Tinted backgrounds and bright borders for outline buttons in dark mode */
      body.dark-theme.dark-theme.dark-theme .btn-danger,
      body.dark-theme.dark-theme.dark-theme .btn-danger-info,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-stroked-button.btn-danger,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-stroked-button.btn-danger-info {
        background-color: rgba(255, 77, 79, 0.15) !important;
        border-color: #ff4d4f !important;
        color: #ff4d4f !important;
      }
      body.dark-theme.dark-theme.dark-theme .btn-success,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-stroked-button.btn-success {
        background-color: rgba(18, 197, 173, 0.15) !important;
        border-color: #12C5AD !important;
        color: #12C5AD !important;
      }
      body.dark-theme.dark-theme.dark-theme .btn-warning,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-stroked-button.btn-warning {
        background-color: rgba(255, 101, 37, 0.15) !important;
        border-color: #FF6525 !important;
        color: #FF6525 !important;
      }

      body.dark-theme.dark-theme.dark-theme .text-link-color, 
      body.dark-theme.dark-theme.dark-theme a:not(.email-content-scoped):not(.email-content-scoped *), 
      body.dark-theme.dark-theme.dark-theme [class*="link"]:not(.email-content-scoped):not(.email-content-scoped *),
      body.dark-theme.dark-theme.dark-theme .mat-mdc-button:not(.mat-mdc-outlined-button):not(.mat-mdc-raised-button):not(.mat-mdc-unelevated-button):not(.mat-mdc-icon-button) {
        color: #5eb3ff !important;
      }
      body.dark-theme.dark-theme.dark-theme .status-pending {
        color: #4da3ff !important;
      }
      body.dark-theme.dark-theme.dark-theme .text-bg-info:not(.email-content-scoped):not(.email-content-scoped *) {
        color: #02253a !important;
      }
      body.dark-theme.dark-theme.dark-theme .text-danger, 
      body.dark-theme.dark-theme.dark-theme .error {
        color: #df2727 !important;
      }
      body.dark-theme.dark-theme.dark-theme .text-info {
        color: #0dcaf0 !important;
      }
      body.dark-theme.dark-theme.dark-theme .status-not-approved {
        color: #e72323 !important;
      }
      body.dark-theme.dark-theme.dark-theme .text-danger-emphasis {
        color: #f76c6c !important;
      }
      body.dark-theme.dark-theme.dark-theme .text-bg-danger:not(.email-content-scoped):not(.email-content-scoped *) {
        color: #f07575 !important;
      }

      /* Radio Buttons in Dark Mode */
      body.dark-theme.dark-theme.dark-theme .mat-mdc-radio-button.mat-mdc-radio-checked .mdc-radio__native-control:enabled:checked+.mdc-radio__background .mdc-radio__inner-circle,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-radio-button.mat-mdc-radio-checked .mdc-radio__native-control:enabled:checked+.mdc-radio__background .mdc-radio__outer-circle {
        border-color: #FF6525 !important;
      }
      body.dark-theme.dark-theme.dark-theme .mat-mdc-radio-button.mat-accent.mat-mdc-radio-checked .mdc-radio__native-control:enabled:checked+.mdc-radio__background .mdc-radio__inner-circle,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-radio-button.mat-accent.mat-mdc-radio-checked .mdc-radio__native-control:enabled:checked+.mdc-radio__background .mdc-radio__outer-circle {
        border-color: #FF6525 !important;
      }
      body.dark-theme.dark-theme.dark-theme .mat-mdc-radio-checked .mat-internal-form-field > label.mdc-label {
        color: #FF6525 !important;
      }


      body.dark-theme.dark-theme.dark-theme .emp-count,
      body.dark-theme.dark-theme.dark-theme .notification-alert {
        background-color: #FF6525 !important;
        color: #ffffff !important;
      }
      
      body.dark-theme.dark-theme.dark-theme .mat-mdc-chip,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-chip-row {
        background-color: #2c2a2a !important;
        border-color: #3a3a3a !important;
        color: #ece7e7 !important;
      }

      /* Insulate the email content to render like a native light-mode document */
      body.dark-theme .email-content-scoped {
          background-color: #ffffff !important;
          color: #000000 !important;
          border-radius: 8px;
          padding: 16px;
      }

      /* Keep the editor area in light mode (WYSIWYG) so inline colors, highlights, and signatures render exactly as they will be sent */
      body.dark-theme .NgxEditor__Content,
      body.dark-theme .note-editable {
          color: #000000 !important;
          background-color: #ffffff !important;
          border-radius: 4px;
          padding: 10px;
      }

      body.dark-theme.dark-theme.dark-theme .mdc-tooltip__surface, 
      body.dark-theme.dark-theme.dark-theme .mat-mdc-tooltip {
        background-color: #000000 !important;
        color: #ffffff !important;
      }
      
      /* Bootstrap and Summernote Lite tooltips */
      body.dark-theme.dark-theme.dark-theme .tooltip .tooltip-inner,
      body.dark-theme.dark-theme.dark-theme .note-tooltip .note-tooltip-content {
        background-color: #1f1d1d !important;
        color: #ffffff !important;
        border: 1px solid #3a3a3a !important;
        box-shadow: 0 2px 4px rgba(0,0,0,0.5) !important;
      }
      body.dark-theme.dark-theme.dark-theme .note-tooltip .note-tooltip-arrow {
        display: none !important;
      }
      body.dark-theme.dark-theme.dark-theme .mat-mdc-header-cell.mat-column-status-isAvailable {
        color: #ece7e7 !important;
        border-color: #3a3a3a !important;
      }
      
      body.dark-theme.dark-theme.dark-theme .mat-mdc-menu-item,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-menu-item .mdc-list-item__primary-text,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-menu-item-text,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-option,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-option .mdc-list-item__primary-text,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-option-text {
        color: #ece7e7 !important;
      }

      body.dark-theme .mat-mdc-button-touch-target {
        background-color: transparent !important;
        border-color: transparent !important;
      }
      
      body.dark-theme.dark-theme.dark-theme .mat-mdc-outlined-button:not(:disabled),
      body.dark-theme.dark-theme.dark-theme .mdc-button--outlined.btn-danger,
      body.dark-theme.dark-theme.dark-theme .mdc-button--outlined.btn-success,
      body.dark-theme.dark-theme.dark-theme .mdc-button--outlined.btn-warning,
      body.dark-theme.dark-theme.dark-theme .btn-outline-secondary,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-icon-button {
        background-color: transparent !important;
      }


      body.dark-theme.dark-theme.dark-theme .btn-compose-mail,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-unelevated-button.mat-primary, 
      body.dark-theme.dark-theme.dark-theme .mat-mdc-raised-button.mat-primary, 
      body.dark-theme.dark-theme.dark-theme .mat-mdc-flat-button.mat-primary {
        background-color: #FF6525 !important;
        color: #ffffff !important;
        border-color: #FF6525 !important;
      }
      body.dark-theme.dark-theme.dark-theme .btn-compose-mail *,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-unelevated-button.mat-primary *, 
      body.dark-theme.dark-theme.dark-theme .mat-mdc-raised-button.mat-primary *, 
      body.dark-theme.dark-theme.dark-theme .mat-mdc-flat-button.mat-primary * {
        background-color: transparent !important;
        color: #ffffff !important;
      }

      /* Fix search bar and outline buttons */
      body.dark-theme.dark-theme.dark-theme .search-bar {
        background-color: #1c1c1c !important;
        border-color: #3a3a3a !important;
      }
      body.dark-theme.dark-theme.dark-theme .btn-outline-secondary,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-stroked-button {
        background-color: transparent !important;
        border-color: #3a3a3a !important;
        color: #ece7e7 !important;
      }
      
      /* Hover states for sidebar options and expansion panels */
      body.dark-theme.dark-theme.dark-theme .mat-expansion-panel-header,
      body.dark-theme.dark-theme.dark-theme .mat-expansion-panel-header.mat-expanded,
      body.dark-theme.dark-theme.dark-theme mat-expansion-panel-header,
      body.dark-theme.dark-theme.dark-theme mat-expansion-panel-header.mat-expanded {
        background-color: transparent !important;
      }
      body.dark-theme.dark-theme.dark-theme .mat-expansion-panel-header:hover,
      body.dark-theme.dark-theme.dark-theme .mat-expansion-panel:not(.mat-expanded) .mat-expansion-panel-header:not([aria-disabled=true]):hover,
      body.dark-theme.dark-theme.dark-theme .mat-expansion-panel-header.mat-expanded,
      body.dark-theme.dark-theme.dark-theme .mat-expansion-panel-header.mat-expanded:hover,
      body.dark-theme.dark-theme.dark-theme .expansion-content .mat-mdc-button:hover,
      body.dark-theme.dark-theme.dark-theme .expansion-content .mat-mdc-button.active {
        background-color: #2c2a2a !important;
      }
      body.dark-theme.dark-theme.dark-theme .mat-expansion-panel-header:hover *:not(.badge),
      body.dark-theme.dark-theme.dark-theme .mat-expansion-panel-header:hover::before,
      body.dark-theme.dark-theme.dark-theme .mat-expansion-panel-header:hover::after,
      body.dark-theme.dark-theme.dark-theme .mat-expansion-panel-header:hover .mat-mdc-focus-indicator {
        background-color: transparent !important;
      }
      body.dark-theme.dark-theme.dark-theme .mdc-button__ripple,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-button-ripple,
      body.dark-theme.dark-theme.dark-theme .mat-ripple,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-button-touch-target {
        background-color: transparent !important;
      }

      /* Elevated surfaces (Popups, Dropdowns, Menus) */
      body.dark-theme.dark-theme.dark-theme .NgxEditor__Popup,
      body.dark-theme.dark-theme.dark-theme .NgxEditor__Dropdown--DropdownMenu,
      body.dark-theme.dark-theme.dark-theme .note-dropdown-menu,
      body.dark-theme.dark-theme.dark-theme .more-details,
      body.dark-theme.dark-theme.dark-theme .suggested-emails,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-menu-panel, 
      body.dark-theme.dark-theme.dark-theme .mat-mdc-select-panel, 
      body.dark-theme.dark-theme.dark-theme .mat-mdc-autocomplete-panel {
        background-color: #2c2a2a !important;
        border: 1px solid #3a3a3a !important;
        box-shadow: 0 4px 12px rgba(0,0,0,0.5) !important;
      }

      body.dark-theme.dark-theme.dark-theme .policy-link,
      body.dark-theme.dark-theme.dark-theme .item-row {
        background: #2c2a2a !important;
        border-color: #3a3a3a !important;
      }
      body.dark-theme.dark-theme.dark-theme .policy-link:hover {
        background: #363434 !important;
        border-color: #FF6525 !important;
      }

      body.dark-theme.dark-theme.dark-theme .sidebar, 
      body.dark-theme.dark-theme.dark-theme header.header, 
      body.dark-theme.dark-theme.dark-theme .main-container, 
      body.dark-theme.dark-theme.dark-theme .rht-container,
      body.dark-theme.dark-theme.dark-theme .card-wrapper, 
      body.dark-theme.dark-theme.dark-theme .mat-mdc-card, 
      body.dark-theme.dark-theme.dark-theme mat-card, 
      body.dark-theme.dark-theme.dark-theme table:not(.email-content-scoped *), 
      body.dark-theme.dark-theme.dark-theme .mat-mdc-table,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-row, 
      body.dark-theme.dark-theme.dark-theme .mat-mdc-header-row, 
      body.dark-theme.dark-theme.dark-theme .mat-mdc-paginator,
      body.dark-theme.dark-theme.dark-theme .paginator-container, 
      body.dark-theme.dark-theme.dark-theme .mat-mdc-dialog-container, 
      body.dark-theme.dark-theme.dark-theme .mat-mdc-dialog-surface,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-dialog-panel,
      body.dark-theme.dark-theme.dark-theme .custom-dialog-container .mdc-dialog__surface,
      body.dark-theme.dark-theme.dark-theme .mdc-dialog__surface,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-dialog-title,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-dialog-content,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-dialog-actions,
      body.dark-theme.dark-theme.dark-theme .mat-dialog-title,
      body.dark-theme.dark-theme.dark-theme .mat-dialog-content,
      body.dark-theme.dark-theme.dark-theme .mat-dialog-actions,
      body.dark-theme.dark-theme.dark-theme .other-information-section, 
      body.dark-theme.dark-theme.dark-theme .add-comments-box-wrapper, 
      body.dark-theme.dark-theme.dark-theme .mail-compose-box-wrapper,
      body.dark-theme.dark-theme.dark-theme .mail-compose-box-wrapper .mail-compose-box-body,
      body.dark-theme.dark-theme.dark-theme .mail-compose-box-wrapper .note-toolbar,
      body.dark-theme.dark-theme.dark-theme .mail-compose-box-wrapper .mail-compose-box-footer,
      body.dark-theme.dark-theme.dark-theme .mail-compose-box-wrapper .attached-file-list,
      body.dark-theme.dark-theme.dark-theme .filter-wrapper, 
      body.dark-theme.dark-theme.dark-theme .page-title-wrapper, 
      body.dark-theme.dark-theme.dark-theme .application-filter-wrapper,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-header-cell.mat-mdc-table-sticky,
      body.dark-theme.dark-theme.dark-theme .mail-preview-container .email-card,
      body.dark-theme.dark-theme.dark-theme .duplicate-application-report-section .applicant-info,
      body.dark-theme.dark-theme.dark-theme .duplicate-application-report-section .duplicate-applicant-info,
      body.dark-theme.dark-theme.dark-theme .duplicate-application-report-section .report-card,
      body.dark-theme.dark-theme.dark-theme .application-summary-section .mat-expansion-panel,
      body.dark-theme.dark-theme.dark-theme .comments-count-wrapper {
        background-color: #1f1d1d !important;
      }
      body.dark-theme.dark-theme.dark-theme .add-comments-box-wrapper {
        border: 1px solid #FF6525 !important;
        border-bottom: none !important;
      }

      body.dark-theme.dark-theme.dark-theme .duplicate-application-report-section .applicant-info,
      body.dark-theme.dark-theme.dark-theme .duplicate-application-report-section .duplicate-applicant-info,
      body.dark-theme.dark-theme.dark-theme .duplicate-application-report-section .report-card,
      body.dark-theme.dark-theme.dark-theme .application-summary-section .mat-expansion-panel,
      body.dark-theme.dark-theme.dark-theme .comments-count-wrapper {
        border-color: #3a3a3a !important;
      }

      body.dark-theme.dark-theme.dark-theme .mail-preview-container {
        background: #0a0a0a !important;
      }
      body.dark-theme.dark-theme.dark-theme .mail-preview-container .email-header {
        background-color: #ff6c35 !important;
      }
      body.dark-theme.dark-theme.dark-theme .mail-preview-container .email-metadata-section,
      body.dark-theme.dark-theme.dark-theme .mail-preview-container .email-footer {
        background-color: #1c1c1c !important;
      }
      body.dark-theme.dark-theme.dark-theme .mail-preview-container .email-divider {
        border-color: #3a3a3a !important;
      }

      body.dark-theme.dark-theme.dark-theme .mat-mdc-dialog-container,
      body.dark-theme.dark-theme.dark-theme .mdc-dialog__surface, 
      body.dark-theme.dark-theme.dark-theme .mat-mdc-menu-panel, 
      body.dark-theme.dark-theme.dark-theme .mat-mdc-select-panel, 
      body.dark-theme.dark-theme.dark-theme .mat-mdc-autocomplete-panel, 
      body.dark-theme.dark-theme.dark-theme .mat-datepicker-content,
      body.dark-theme.dark-theme.dark-theme .cdk-overlay-pane {
        border-color: #FF6525 !important;
        border-width: 1px !important;
        border-style: solid !important;
      }

      body.dark-theme.dark-theme.dark-theme .spinner {
        border-color: #3a3a3a !important;
        border-top-color: #FF6525 !important;
      }

      body.dark-theme.dark-theme.dark-theme input:not(.away-lock-screen input),
      body.dark-theme.dark-theme.dark-theme textarea,
      body.dark-theme.dark-theme.dark-theme .mdc-text-field {
        background-color: #1c1c1c !important;
      }

      /* CAMS ID visibility fix */
      body.dark-theme.dark-theme.dark-theme .cam-id,
      body.dark-theme.dark-theme.dark-theme .cam-id .text-dark {
        color: #ece7e7 !important;
      }

      body.dark-theme.dark-theme.dark-theme input:not(.away-lock-screen input),
      body.dark-theme.dark-theme.dark-theme textarea,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-select-value-text,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-select-value {
        color: #ffffff !important;
      }
      body.dark-theme input:not(.away-lock-screen input),
      body.dark-theme textarea {
        caret-color: #ffffff !important;
      }

      body.dark-theme input::placeholder,
      body.dark-theme textarea::placeholder,
      body.dark-theme .mat-mdc-input-element::placeholder {
        color: rgba(255, 255, 255, 0.4) !important;
      }

      body.dark-theme.dark-theme.dark-theme tr.mat-mdc-row:hover, 
      body.dark-theme.dark-theme.dark-theme .mat-mdc-row:hover,
      body.dark-theme.dark-theme.dark-theme .mail-list:hover {
        background-color: #2c2a2a !important;
      }
      
      /* Unread and Active Mail list items */
      body.dark-theme.dark-theme.dark-theme .mail-list.unread {
        background-color: rgba(255, 255, 255, 0.15) !important;
      }
      body.dark-theme.dark-theme.dark-theme .mail-list.unread .mlrc-name,
      body.dark-theme.dark-theme.dark-theme .mail-list.unread h6,
      body.dark-theme.dark-theme.dark-theme .mail-list.unread p {
        color: #ffffff !important;
      }
      body.dark-theme.dark-theme.dark-theme .mail-list:not(.unread) .mlrc-name,
      body.dark-theme.dark-theme.dark-theme .mail-list:not(.unread) h6 {
        color: #a0aab2 !important;
      }
      body.dark-theme.dark-theme.dark-theme .mail-list:not(.unread) p {
        color: #7b848c !important;
      }
      body.dark-theme.dark-theme.dark-theme .mail-list.active {
        background-color: rgba(255, 101, 37, 0.15) !important;
      }
      
      /* Sidebar Counter Badges */
      body.dark-theme.dark-theme.dark-theme .counter {
        color: #ece7e7 !important;
        background-color: #2c2a2a !important;
        border-color: #3a3a3a !important;
      }
      body.dark-theme.dark-theme.dark-theme .mat-mdc-button.active .counter {
        color: #FF6525 !important;
        background-color: rgba(255, 101, 37, 0.15) !important;
        border-color: #FF6525 !important;
      }
      
      body.dark-theme.dark-theme.dark-theme th:not(.email-content-scoped th),
      body.dark-theme.dark-theme.dark-theme [class*="mat-mdc-header"],
      body.dark-theme.dark-theme.dark-theme [class*="mat-header"] {
        background-color: #1a1a1a !important;
      }

      body.dark-theme.dark-theme.dark-theme td:not(.email-content-scoped td),
      body.dark-theme.dark-theme.dark-theme .mat-mdc-cell {
        background-color: transparent !important;
      }

      body.dark-theme.dark-theme.dark-theme .mat-mdc-option:hover,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-option.mdc-list-item--selected,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-option.mat-mdc-option-active {
        color: #FF6525 !important;
        background-color: rgba(255, 101, 37, 0.1) !important;
      }
      body.dark-theme.dark-theme.dark-theme .mat-mdc-option:hover .mdc-list-item__primary-text,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-option.mdc-list-item--selected .mdc-list-item__primary-text,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-option.mat-mdc-option-active .mdc-list-item__primary-text {
        color: #FF6525 !important;
      }



      /* Condition-based reddish background for duplicate warnings */
      body.dark-theme.dark-theme.dark-theme .duplicate-warning,
      body.dark-theme.dark-theme.dark-theme .duplicate-warning .mat-expansion-panel-header,
      body.dark-theme.dark-theme.dark-theme .duplicate-warning .mat-expansion-panel-header.mat-expanded {
        background-color: #e848486b !important;
      }
      body.dark-theme.dark-theme.dark-theme .duplicate-warning .application-summary-section,
      body.dark-theme.dark-theme.dark-theme .duplicate-warning .mat-expansion-panel,
      body.dark-theme.dark-theme.dark-theme .duplicate-warning .mat-expansion-panel-content,
      body.dark-theme.dark-theme.dark-theme .duplicate-warning .mat-expansion-panel-body,
      body.dark-theme.dark-theme.dark-theme .duplicate-warning .mat-mdc-expansion-panel-content {
        background-color: #ff000030 !important;
      }

      /* Tab labels always pure white */
      body.dark-theme.dark-theme.dark-theme .mdc-tab__text-label {
        color: #ffffff !important;
      }

      /* Selected tab orange underline */
      body.dark-theme.dark-theme.dark-theme .mdc-tab-indicator--active .mdc-tab-indicator__content--underline {
        border-color: #FF6525 !important;
      }

      /* Mandatory asterisk and text danger in red */
      body.dark-theme.dark-theme.dark-theme .text-danger, 
      body.dark-theme.dark-theme.dark-theme .text-danger *,
      body.dark-theme.dark-theme.dark-theme .mat-mdc-form-field-required-marker,
      body.dark-theme.dark-theme.dark-theme span[style*="color:red"],
      body.dark-theme.dark-theme.dark-theme span[style*="color: red"],
      body.dark-theme.dark-theme.dark-theme mat-label span {
        color: #ff4d4f !important;
      }

      /* Delegate Access (Switch User) Banner */
      body.dark-theme.dark-theme.dark-theme .switch-user-wrapper,
      body.dark-theme.dark-theme.dark-theme .header-top-note-section {
        background-color: #dc3545 !important;
        color: #ffffff !important;
      }
      body.dark-theme.dark-theme.dark-theme .header-top-note-section p,
      body.dark-theme.dark-theme.dark-theme .header-top-note-section span {
        color: #ffffff !important;
      }
      body.dark-theme.dark-theme.dark-theme .header-top-note-section .btn-exit {
        color: #ffffff !important;
      }

      /* Sign Off Dialog */
      body.dark-theme.dark-theme.dark-theme .soc-dialog .soc-card {
        background-color: #1f1d1d !important;
        border-color: #3a3a3a !important;
      }
      body.dark-theme.dark-theme.dark-theme .soc-dialog .soc-card:hover {
        background-color: #2c2a2a !important;
        border-color: #FF6525 !important;
      }
      body.dark-theme.dark-theme.dark-theme .soc-card-icon.soc-icon-green {
        background-color: #16A34A !important;
      }
      body.dark-theme.dark-theme.dark-theme .soc-card-icon.soc-icon-orange {
        background-color: #FF6525 !important;
      }
      body.dark-theme.dark-theme.dark-theme .soc-dialog .soc-card-label {
        color: #ffffff !important;
      }
      body.dark-theme.dark-theme.dark-theme .soc-dialog .soc-card-sub {
        color: #a0aab2 !important;
      }
      body.dark-theme.dark-theme.dark-theme .soc-dialog .soc-cancel-btn {
        color: #e0e0e0 !important;
      }

      /* Logout Button in User Menu */
      body.dark-theme.dark-theme.dark-theme .logout-btn span,
      body.dark-theme.dark-theme.dark-theme .logout-btn mat-icon {
        color: #ff4d4f !important;
      }
      body.dark-theme.dark-theme.dark-theme .logout-btn:hover {
        background-color: rgba(255, 77, 79, 0.1) !important;
      }

      /* Set Away Status Dialog */
      body.dark-theme.dark-theme.dark-theme .away-dialog-content .away-card {
        background-color: #1f1d1d !important;
        border-color: #3a3a3a !important;
      }
      body.dark-theme.dark-theme.dark-theme .away-dialog-content .away-card:hover {
        background-color: #2c2a2a !important;
        border-color: #FF6525 !important;
      }
      body.dark-theme.dark-theme.dark-theme .away-dialog-content .away-card-label {
        color: #ffffff !important;
      }
      body.dark-theme.dark-theme.dark-theme .away-dialog-content .away-card-sub {
        color: #a0aab2 !important;
      }
      body.dark-theme.dark-theme.dark-theme .away-dialog-actions .away-cancel-btn {
        color: #e0e0e0 !important;
      }

      /* Login Page */
      body.dark-theme.dark-theme.dark-theme app-login .login-wrapper {
        background: linear-gradient(to bottom, #FF6525, #1f1d1d) !important;
        border: none !important;
      }
      body.dark-theme.dark-theme.dark-theme app-login .login-wrapper-inner {
        background-color: #1a1b1f !important;
      }

      /* Fix close buttons visibility */
      body.dark-theme.dark-theme.dark-theme .close,
      body.dark-theme.dark-theme.dark-theme .btn-close {
        color: #ece7e7 !important;
        opacity: 0.8 !important;
        text-shadow: none !important;
      }

      /* Other Auth Pages (Authenticator, Forgot Password, etc.) */
      body.dark-theme.dark-theme.dark-theme app-authenticator .login-wrapper,
      body.dark-theme.dark-theme.dark-theme app-forgot-password .login-wrapper,
      body.dark-theme.dark-theme.dark-theme app-reset-password .login-wrapper,
      body.dark-theme.dark-theme.dark-theme app-email-reset-password .login-wrapper {
        background: #1a1b1f !important;
        border-color: #3a3a3a !important;
      }

      /* Fix Internal Chat UI in Dark Mode (WhatsApp-style colors) */
      /* Sidebar / Drawers */
      body.dark-theme.dark-theme.dark-theme .chat-sidebar-actions .nc-add-btn {
        color: #aebac1 !important;
      }
      body.dark-theme.dark-theme.dark-theme .chat-sidebar-actions .nc-add-btn:hover {
        color: #e9edef !important;
      }
      body.dark-theme.dark-theme.dark-theme .nc-drawer,
      body.dark-theme.dark-theme.dark-theme .nc-panel {
        background-color: #111b21 !important;
      }
      body.dark-theme.dark-theme.dark-theme .nc-header {
        background-color: #202c33 !important;
        color: #e9edef !important;
      }
      body.dark-theme.dark-theme.dark-theme .nc-header .nc-back-btn,
      body.dark-theme.dark-theme.dark-theme .nc-header .nc-title {
        color: #e9edef !important;
      }
      body.dark-theme.dark-theme.dark-theme .nc-search input {
        background-color: #202c33 !important;
        color: #e9edef !important;
        border: 1px solid #3a3a3a !important;
      }

      /* Chat Area */
      body.dark-theme.dark-theme.dark-theme .messenger-wrapper .messenger-wrapper-top {
        background-color: #0b141a !important; 
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='200' height='200'%3E%3Cdefs%3E%3Cg id='leaf'%3E%3Cpath d='M-8 3 Q0 -6 8 3 Q0 8 -8 3 Z'/%3E%3Cpath d='M0 3 L0 8'/%3E%3C/g%3E%3Cg id='flower'%3E%3Ccircle cx='0' cy='-4' r='2.2'/%3E%3Ccircle cx='3.8' cy='-2' r='2.2'/%3E%3Ccircle cx='3.8' cy='2' r='2.2'/%3E%3Ccircle cx='0' cy='4' r='2.2'/%3E%3Ccircle cx='-3.8' cy='2' r='2.2'/%3E%3Ccircle cx='-3.8' cy='-2' r='2.2'/%3E%3C/g%3E%3Cg id='envelope'%3E%3Crect x='-7' y='-4.5' width='14' height='9' rx='1'/%3E%3Cpath d='M-7 -4.5 L0 1.5 L7 -4.5'/%3E%3C/g%3E%3Cg id='note'%3E%3Ccircle cx='-3' cy='5' r='2'/%3E%3Cpath d='M-1 5 L-1 -6 L4.5 -4.5 L4.5 -0.5'/%3E%3Ccircle cx='2.5' cy='0.5' r='2'/%3E%3C/g%3E%3Cg id='camera'%3E%3Crect x='-8' y='-4' width='16' height='10' rx='1.5'/%3E%3Crect x='-3' y='-6.5' width='6' height='3' rx='0.5'/%3E%3Ccircle cx='0' cy='1' r='3'/%3E%3C/g%3E%3Cg id='heart'%3E%3Cpath d='M0 6 C-2 3 -6 1 -6 -3 C-6 -5.5 -3 -7 -1 -5 C-0.5 -4.5 0 -4 0 -4 C0 -4 0.5 -4.5 1 -5 C3 -7 6 -5.5 6 -3 C6 1 2 3 0 6 Z'/%3E%3C/g%3E%3Cg id='star'%3E%3Cpath d='M0 -6 L1.5 -2 L6 -2 L2.5 1 L4 5 L0 2.5 L-4 5 L-2.5 1 L-6 -2 L-1.5 -2 Z'/%3E%3C/g%3E%3Cg id='cloud'%3E%3Ccircle cx='-4' cy='0' r='3.5'/%3E%3Ccircle cx='2' cy='-2' r='4.5'/%3E%3Ccircle cx='7' cy='0' r='3'/%3E%3Crect x='-7' y='0' width='17' height='4' rx='2'/%3E%3C/g%3E%3Cg id='plane'%3E%3Cpath d='M-10 4 L11 -6 L4 5 L1 0 Z'/%3E%3C/g%3E%3C/defs%3E%3Cg fill='none' stroke='%23445055' stroke-width='1' stroke-linecap='round' stroke-linejoin='round' opacity='0.15'%3E%3Cuse xlink:href='%23leaf' x='20' y='20'/%3E%3Cuse xlink:href='%23flower' x='95' y='18'/%3E%3Cuse xlink:href='%23star' x='165' y='28'/%3E%3Cuse xlink:href='%23envelope' x='45' y='58'/%3E%3Cuse xlink:href='%23note' x='120' y='62'/%3E%3Cuse xlink:href='%23leaf' x='178' y='75'/%3E%3Cuse xlink:href='%23camera' x='15' y='102'/%3E%3Cuse xlink:href='%23heart' x='75' y='108'/%3E%3Cuse xlink:href='%23cloud' x='150' y='112'/%3E%3Cuse xlink:href='%23flower' x='28' y='148'/%3E%3Cuse xlink:href='%23star' x='100' y='150'/%3E%3Cuse xlink:href='%23plane' x='165' y='158'/%3E%3Cuse xlink:href='%23note' x='55' y='182'/%3E%3Cuse xlink:href='%23envelope' x='130' y='180'/%3E%3C/g%3E%3C/svg%3E") !important;
      }
      body.dark-theme.dark-theme.dark-theme .messenger-wrapper .messenger-wrapper-bottom,
      body.dark-theme.dark-theme.dark-theme .messenger-wrapper .messenger-wrapper-header {
        background-color: #202c33 !important;
        border-color: #202c33 !important;
      }
      
      /* Sender (Other person) bubbles */
      body.dark-theme.dark-theme.dark-theme .messenger-wrapper .msg-box-content.sender .mbc-inner {
        background-color: #202c33 !important;
        border: none !important;
        color: #e9edef !important;
      }
      body.dark-theme.dark-theme.dark-theme .messenger-wrapper .msg-box-content.sender .mbc-inner::before {
        background-color: #202c33 !important;
      }
      body.dark-theme.dark-theme.dark-theme .messenger-wrapper .msg-box-content.sender .sender-name {
        color: #53bdeb !important;
      }
      
      /* Receiver (Me) bubbles */
      body.dark-theme.dark-theme.dark-theme .messenger-wrapper .msg-box-content.receiver .mbc-inner {
        background-color: #005c4b !important;
        border: none !important;
        color: #e9edef !important;
      }
      body.dark-theme.dark-theme.dark-theme .messenger-wrapper .msg-box-content.receiver .mbc-inner::before {
        background-color: #005c4b !important;
      }
      
      /* Input area */
      body.dark-theme.dark-theme.dark-theme .messenger-wrapper .messenger-wrapper-bottom textarea {
        background-color: #2a3942 !important;
        color: #e9edef !important;
        border-radius: 8px !important;
        padding-left: 10px !important;
      }
      
      /* System messages */
      body.dark-theme.dark-theme.dark-theme .messenger-wrapper .msg-system-activity span {
        background-color: #1a1b1f !important;
        border: 1px solid #3a3a3a !important;
        color: #ece7e7 !important;
      }
      
      /* Emoji Picker */
      body.dark-theme.dark-theme.dark-theme .emoji-picker-popup,
      body.dark-theme.dark-theme.dark-theme app-emoji-picker .ep-container {
        background-color: #1f1d1d !important;
        border-color: #3a3a3a !important;
        box-shadow: 0 4px 15px rgba(0,0,0,0.5) !important;
      }
      body.dark-theme.dark-theme.dark-theme app-emoji-picker .ep-search input {
        background-color: #2a3942 !important;
        color: #e9edef !important;
        border: 1px solid #3a3a3a !important;
      }
      body.dark-theme.dark-theme.dark-theme app-emoji-picker .ep-cat-btn {
        color: #8696a0 !important;
      }
      body.dark-theme.dark-theme.dark-theme app-emoji-picker .ep-cat-btn.active {
        color: #00a884 !important;
      }
      body.dark-theme.dark-theme.dark-theme app-emoji-picker .ep-group-title {
        color: #8696a0 !important;
        background-color: #1f1d1d !important;
      }

      /* Start Day Page */
      body.dark-theme.dark-theme.dark-theme app-start-day .sd-wrapper {
        background: linear-gradient(to bottom, #FF6525, #1f1d1d) !important;
        border: none !important;
      }
      body.dark-theme.dark-theme.dark-theme app-start-day .sd-wrapper-inner {
        background-color: #1a1b1f !important;
      }
      body.dark-theme.dark-theme.dark-theme app-start-day .sd-bau-card {
        background-color: #1f1d1d !important;
        border-color: #3a3a3a !important;
      }
      body.dark-theme.dark-theme.dark-theme app-start-day .sd-bau-card:has(.mat-mdc-checkbox-checked) {
        border-color: #16A34A !important;
        background-color: rgba(22, 163, 74, 0.15) !important;
      }
      body.dark-theme.dark-theme.dark-theme app-start-day .sd-punch-time {
        color: #16A34A !important;
      }

      /* Browser Autofill Fix for Dark Mode */
      body.dark-theme input:-webkit-autofill,
      body.dark-theme input:-webkit-autofill:hover, 
      body.dark-theme input:-webkit-autofill:focus, 
      body.dark-theme input:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 30px #1c1c1c inset !important;
        -webkit-text-fill-color: #ffffff !important;
        transition: background-color 5000s ease-in-out 0s;
      }

      /* Calendar / Datepicker Fix for Dark Mode */
      body.dark-theme.dark-theme.dark-theme .mat-datepicker-content,
      body.dark-theme.dark-theme.dark-theme .mat-calendar {
        background-color: #1f1d1d !important;
        color: #ece7e7 !important;
      }
      body.dark-theme.dark-theme.dark-theme .mat-calendar-table-header th,
      body.dark-theme.dark-theme.dark-theme .mat-calendar-body-label,
      body.dark-theme.dark-theme.dark-theme .mat-calendar-body-cell-content,
      body.dark-theme.dark-theme.dark-theme .mat-calendar-arrow {
        color: #ece7e7 !important;
      }
      body.dark-theme.dark-theme.dark-theme .mat-calendar-body-cell:hover .mat-calendar-body-cell-content:not(.mat-calendar-body-selected) {
        background-color: rgba(255, 255, 255, 0.1) !important;
      }
    `;
  }
}